// Pure Google Identity Services (GIS) integration for Google Calendar.
// No Firebase Auth involved — we only need an OAuth access token with the
// calendar.readonly scope, which the GIS token client gives us directly.
//
// Setup: put your Google Cloud OAuth Client ID (Web application) in
// client/.env as REACT_APP_GOOGLE_CLIENT_ID and add the app domain
// (e.g. https://teamtrack.elmecelettronica.com) to the client's
// "Authorized JavaScript origins" in Google Cloud Console.

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "989212354221-k67e91baa9j4cij3t38neqik2vp8kb6l.apps.googleusercontent.com"
// openid/email/profile are needed alongside the calendar scope so Google's
// tokeninfo/userinfo endpoints return the user's identity (used for app
// login and to show which account is connected).
const GOOGLE_SCOPES = "openid email profile https://www.googleapis.com/auth/calendar.readonly"
const GOOGLE_CALENDAR_AUTH_KEY = "googleCalendarAuth"

let gisScriptPromise = null

// Loads the GIS script once and caches the promise.
const loadGisScript = () => {
  if (gisScriptPromise) return gisScriptPromise

  gisScriptPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve()
      return
    }

    const script = document.createElement("script")
    script.src = "https://accounts.google.com/gsi/client"
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      gisScriptPromise = null
      reject(new Error("Failed to load Google Identity Services"))
    }
    document.head.appendChild(script)
  })

  return gisScriptPromise
}

// Fetches basic profile info (email/name/photo) so the UI can show which
// Google account is connected.
const fetchGoogleProfile = async (accessToken) => {
  try {
    const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    if (!res.ok) return null
    const profile = await res.json()
    return { name: profile?.name, email: profile?.email, photo: profile?.picture }
  } catch {
    return null
  }
}

const signInWithGoogleCalendar = async () => {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error("Missing REACT_APP_GOOGLE_CLIENT_ID — add it to client/.env")
  }

  await loadGisScript()

  const tokenResponse = await new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: GOOGLE_SCOPES,
      callback: (response) => {
        if (response?.error) {
          // User closed the popup or denied access.
          reject(new Error(response.error))
          return
        }
        resolve(response)
      },
    })
    client.requestAccessToken()
  })

  const accessToken = tokenResponse?.access_token
  if (!accessToken) {
    throw new Error("Google did not return a Calendar access token")
  }

  const googleUser = await fetchGoogleProfile(accessToken)

  const authData = { accessToken, googleUser }
  sessionStorage.setItem(GOOGLE_CALENDAR_AUTH_KEY, JSON.stringify(authData))
  return authData
}

const getStoredGoogleCalendarAuth = () => {
  try {
    return JSON.parse(sessionStorage.getItem(GOOGLE_CALENDAR_AUTH_KEY))
  } catch {
    return null
  }
}

const clearGoogleCalendarAuth = () => {
  sessionStorage.removeItem(GOOGLE_CALENDAR_AUTH_KEY)
}

export { signInWithGoogleCalendar, getStoredGoogleCalendarAuth, clearGoogleCalendarAuth }
